const CDN = "https://d1j77pbqalhmf9.cloudfront.net/unity-cosmetics";

export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  date: string;
  author: string;
  category: "Tutorial" | "Informatie";
  featuredImage: string;
  youtubeId?: string;
}

export const blogPosts: BlogPost[] = [
  {
    slug: "hoe-breng-je-foundation-aan",
    title: "Hoe breng je foundation aan?",
    excerpt:
      "Leer stap voor stap hoe je hypoallergene foundation aanbrengt voor een egale, natuurlijke teint. Geschikt voor de gevoelige huid.",
    content: `
      <p>Foundation is de basis van elke make-up look. Maar als je een gevoelige huid hebt, wil je zeker weten dat je foundation geen irritatie veroorzaakt. In deze tutorial laten we zien hoe je onze hypoallergene foundation aanbrengt voor het beste resultaat.</p>

      <h2>Voorbereiding</h2>
      <p>Begin altijd met een schoon, gehydrateerd gezicht. Breng eerst je dagcreme aan en laat deze goed intrekken voordat je foundation aanbrengt. Dit zorgt voor een gladde basis en voorkomt dat de foundation in droge plekjes trekt.</p>

      <h2>De juiste kleur kiezen</h2>
      <p>Test de kleur altijd op je kaakrand, niet op je hand. Je foundation moet naadloos opgaan in je huid. Bij Unity Cosmetics bieden we meerdere tinten aan die speciaal zijn ontwikkeld voor een natuurlijk resultaat.</p>

      <h2>Aanbrengen</h2>
      <p>Breng kleine stipjes foundation aan op je voorhoofd, wangen, neus en kin. Blend de foundation met een sponsje of kwast vanuit het midden van je gezicht naar buiten. Werk in dunne lagen voor een opbouwbare dekking.</p>

      <h2>Tip voor gevoelige huid</h2>
      <p>Gebruik altijd schone kwasten en sponsjes. Vuile tools kunnen bacterien bevatten die irritatie veroorzaken. Was je kwasten minimaal een keer per week met een milde zeep.</p>
    `,
    date: "2024-06-15",
    author: "Team Unity Cosmetics",
    category: "Tutorial",
    featuredImage: `${CDN}/gezicht/foundation/foundation-200-porcelain.webp`,
  },
  {
    slug: "hoe-breng-je-oogpotlood-aan",
    title: "Hoe breng je oogpotlood aan?",
    excerpt:
      "Tips en technieken voor het aanbrengen van oogpotlood zonder irritatie. Ideaal voor gevoelige ogen en contactlensdragers.",
    content: `
      <p>Oogpotlood kan je blik helemaal veranderen. Maar voor mensen met gevoelige ogen is het belangrijk om de juiste techniek te gebruiken. In deze video tutorial delen we onze beste tips.</p>

      <h2>Het juiste potlood</h2>
      <p>Kies een hypoallergeen oogpotlood dat vrij is van parfum en irriterende stoffen. Onze oogpotloden zijn speciaal ontwikkeld voor de gevoelige huid rondom de ogen en zijn geschikt voor contactlensdragers.</p>

      <h2>Techniek</h2>
      <p>Houd je ooglid licht gespannen en breng het potlood aan in kleine, korte streepjes langs de wimperrand. Begin aan de buitenkant van je oog en werk naar binnen toe. Voor een subtiel effect kun je de lijn uitvegen met een klein kwastje.</p>

      <h2>Waterrand</h2>
      <p>Wil je het potlood op je waterrand aanbrengen? Zorg dan dat het potlood vers geslepen is voor hygiene. Trek je onderooglid voorzichtig naar beneden en breng het potlood aan met zachte bewegingen.</p>

      <h2>Langhoudend resultaat</h2>
      <p>Voor een langere houdbaarheid kun je een dun laagje oogschaduw in dezelfde kleur over het potlood aanbrengen. Dit fixeert de kleur en voorkomt doorlopen.</p>
    `,
    date: "2024-05-20",
    author: "Team Unity Cosmetics",
    category: "Tutorial",
    featuredImage: `${CDN}/ogen/oogpotlood/oogpotlood-396-olive.webp`,
  },
  {
    slug: "hoe-breng-je-concealer-aan",
    title: "Hoe breng je concealer aan?",
    excerpt:
      "Ontdek hoe je concealer gebruikt om oneffenheden te camoufleren, speciaal voor de gevoelige huid. Zonder irritatie, met een natuurlijk resultaat.",
    content: `
      <p>Concealer is onmisbaar om donkere kringen, roodheid en oneffenheden te camoufleren. Met de juiste techniek bereik je een fris, natuurlijk resultaat zonder dat het maskerachtig wordt.</p>

      <h2>Waar breng je concealer aan?</h2>
      <p>De meest voorkomende plekken zijn onder de ogen (voor donkere kringen), rondom de neus (voor roodheid) en op eventuele onzuiverheden. Je kunt concealer ook gebruiken om je wenkbrauwen te highlighten.</p>

      <h2>De juiste tint</h2>
      <p>Voor onder je ogen: kies een tint die een halve shade lichter is dan je foundation. Voor het camoufleren van roodheid: kies een tint die exact overeenkomt met je huidtint.</p>

      <h2>Aanbrengtechniek</h2>
      <p>Breng de concealer aan in een omgekeerde driehoek onder je oog. Klop de concealer zachtjes in met je ringvinger (deze oefent de minste druk uit) of met een klein sponsje. Nooit wrijven, altijd kloppen.</p>

      <h2>Fixeren</h2>
      <p>Set je concealer met een klein beetje transparant poeder om doorlopen te voorkomen. Gebruik een pluizig kwastje en breng het poeder aan met lichte, rollende bewegingen.</p>
    `,
    date: "2024-04-10",
    author: "Team Unity Cosmetics",
    category: "Tutorial",
    featuredImage: `${CDN}/gezicht/concealer/concealer-100-porcelain.webp`,
  },
  {
    slug: "hoe-breng-je-mascara-aan",
    title: "Hoe breng je mascara aan?",
    excerpt:
      "De beste manier om mascara aan te brengen als je gevoelige ogen hebt of contactlenzen draagt. Zonder klonteren, zonder irritatie.",
    content: `
      <p>Mascara maakt misschien wel het grootste verschil in je make-up routine. Maar voor mensen met gevoelige ogen of contactlenzen kan het ook een bron van irritatie zijn. In deze tutorial leer je hoe je onze hypoallergene mascara het beste aanbrengt.</p>

      <h2>Voorbereiding</h2>
      <p>Zorg dat je wimpers schoon en vetvrij zijn. Als je contactlenzen draagt, breng deze altijd eerst in voordat je mascara aanbrengt. Zo voorkom je dat er mascara-deeltjes onder je lenzen terechtkomen.</p>

      <h2>Aanbrengen in drie stappen</h2>
      <p>1. Begin bij de wimperwortel en wiggle het borsteltje zachtjes van links naar rechts terwijl je omhoog trekt. Dit zorgt voor volume bij de basis.</p>
      <p>2. Trek het borsteltje in een vloeiende beweging naar de puntjes voor lengte.</p>
      <p>3. Breng een tweede laag aan voordat de eerste volledig droog is. Dit voorkomt klonteren.</p>

      <h2>Onderwimpers</h2>
      <p>Houd het borsteltje verticaal en gebruik alleen het puntje om je onderwimpers te coaten. Minder is hier meer.</p>

      <h2>Tips voor lensdragers</h2>
      <p>Kies altijd een tubing mascara of one die speciaal is ontwikkeld voor gevoelige ogen. Onze mascara is ophthalmologisch getest en veilig voor contactlensdragers. Vervang je mascara elke drie maanden voor optimale hygiene.</p>
    `,
    date: "2024-03-05",
    author: "Team Unity Cosmetics",
    category: "Tutorial",
    featuredImage: `${CDN}/ogen/mascara/mascara-599-black.webp`,
  },
  {
    slug: "blauwgroene-oogschaduw-look",
    title: "Blauwgroene oogschaduw look",
    excerpt:
      "Stap-voor-stap tutorial voor een opvallende blauwgroene oogschaduw look met hypoallergene producten. Veilig voor gevoelige ogen.",
    content: `
      <p>Een blauwgroene oogschaduw look is perfect voor als je iets meer wilt dan je dagelijkse make-up. In deze tutorial laten we zien hoe je een opvallende maar draagbare look creert met onze hypoallergene oogschaduws.</p>

      <h2>Wat heb je nodig?</h2>
      <p>Voor deze look gebruik je een lichte base-kleur, een blauwgroene oogschaduw voor het bewegende ooglid en een donkere tint voor de oogplooi. Daarnaast een blendkwast en een plat kwastje voor het aanbrengen.</p>

      <h2>Stap 1: Base</h2>
      <p>Breng een lichte, matte oogschaduw aan over je hele ooglid als base. Dit helpt de kleuren beter te blenden en langer te houden.</p>

      <h2>Stap 2: Hoofdkleur</h2>
      <p>Breng de blauwgroene oogschaduw aan op het bewegende ooglid met een plat kwastje. Dep de kleur aan voor maximale kleurafgifte in plaats van vegen.</p>

      <h2>Stap 3: Oogplooi</h2>
      <p>Breng een iets donkerdere tint aan in je oogplooi met een blendkwast. Maak windenwisser-bewegingen om de kleuren naadloos in elkaar te laten overlopen.</p>

      <h2>Stap 4: Afwerking</h2>
      <p>Highlight de binnenooghoek en het midden van je ooglid met een licht shimmer. Dit geeft dimensie aan de look. Werk af met mascara en eventueel een lijn oogpotlood.</p>
    `,
    date: "2024-02-14",
    author: "Team Unity Cosmetics",
    category: "Tutorial",
    featuredImage: `${CDN}/ogen/oogschaduw/oogschaduw-468-ocean-blue.webp`,
  },
  {
    slug: "roze-oogschaduw-look",
    title: "Roze oogschaduw look",
    excerpt:
      "Creeer een zachte, romantische roze oogschaduw look. Hypoallergeen en parfumvrij, perfect voor de gevoelige huid.",
    content: `
      <p>Een roze oogschaduw look is zacht, romantisch en verrassend draagbaar. In deze tutorial leer je hoe je een prachtige roze look creert die geschikt is voor gevoelige ogen.</p>

      <h2>De juiste roze kiezen</h2>
      <p>Roze oogschaduw komt in veel varianten: van zacht pastelroze tot diep mauve. Kies een tint die bij je huidtint past. Lichtere huidtinten staan mooi met koele roze tinten, terwijl warmere huidtinten prachtig zijn met perzikroze.</p>

      <h2>Stap 1: Primer</h2>
      <p>Begin met een oogschaduwbase om de kleur te intensiveren en de houdbaarheid te verlengen. Dit is vooral belangrijk bij lichtere kleuren die anders snel kunnen vervagen.</p>

      <h2>Stap 2: Lichte basis</h2>
      <p>Breng een licht roze over het hele ooglid aan. Dit wordt je basistint waarop je de rest van de look opbouwt.</p>

      <h2>Stap 3: Verdieping</h2>
      <p>Gebruik een diepere roze of mauve in de oogplooi voor definitie. Blend goed uit naar boven voor een zachte overgang.</p>

      <h2>Stap 4: Shimmer accent</h2>
      <p>Breng een roze shimmer aan op het midden van je ooglid en in de binnenooghoek. Dit geeft de look een frisse, glamoureuze finish.</p>

      <h2>Tip</h2>
      <p>Combineer deze look met een nude lipkleur en subtiele blush voor een harmonieus geheel. De roze tonen in je oogschaduw worden zo mooi opgepikt door de rest van je make-up.</p>
    `,
    date: "2024-01-22",
    author: "Team Unity Cosmetics",
    category: "Tutorial",
    featuredImage: `${CDN}/ogen/oogschaduw/oogschaduw-467-rosewood.webp`,
  },
  {
    slug: "hoe-lang-is-make-up-houdbaar",
    title: "Hoe lang is make-up houdbaar?",
    excerpt:
      "Alles wat je moet weten over de houdbaarheid van make-up. Wanneer moet je producten vervangen en hoe herken je verlopen cosmetica?",
    content: `
      <p>Heb je je ooit afgevraagd hoe lang je make-up eigenlijk meegaat? Veel mensen gebruiken producten veel langer dan veilig is. In dit artikel leggen we uit hoe lang verschillende producten houdbaar zijn en waarom het belangrijk is om verlopen make-up te vervangen.</p>

      <h2>Het PAO-symbool</h2>
      <p>Op de verpakking van cosmetica vind je het PAO-symbool (Period After Opening): een potje met een getal en de letter M. Bijvoorbeeld "12M" betekent dat het product 12 maanden na opening houdbaar is. Let hier altijd op bij het openen van een nieuw product.</p>

      <h2>Houdbaarheid per product</h2>

      <h3>Mascara: 3-6 maanden</h3>
      <p>Mascara heeft de kortste houdbaarheid van alle make-up producten. Het donkere, vochtige milieu in de tube is een voedingsbodem voor bacterien. Vervang je mascara elke drie maanden, zeker als je gevoelige ogen hebt of contactlenzen draagt.</p>

      <h3>Foundation: 12-18 maanden</h3>
      <p>Vloeibare foundation is ongeveer een jaar tot anderhalf jaar houdbaar na opening. Merk je dat de textuur verandert, de kleur afwijkt of het product anders ruikt? Tijd om te vervangen.</p>

      <h3>Concealer: 12 maanden</h3>
      <p>Vergelijkbaar met foundation, maar omdat concealer vaak direct rondom de ogen wordt gebruikt, is het extra belangrijk om het tijdig te vervangen.</p>

      <h3>Oogschaduw (poeder): 24 maanden</h3>
      <p>Poederproducten zijn langer houdbaar omdat bacterien minder goed groeien in droge omgevingen. Gebruik wel altijd schone kwasten.</p>

      <h3>Lippenstift en lipgloss: 12-18 maanden</h3>
      <p>Lippenstift is relatief lang houdbaar. Let op veranderingen in geur, textuur of kleur als teken dat het product verlopen is.</p>

      <h3>Oogpotlood en lipliner: 12-24 maanden</h3>
      <p>Doordat je potloden regelmatig slijpt, verwijder je steeds de buitenste laag. Dit maakt ze langer houdbaar dan veel andere producten.</p>

      <h2>Tekenen dat je make-up verlopen is</h2>
      <ul>
        <li>Veranderde geur (ranzig, chemisch)</li>
        <li>Veranderde textuur (klonterig, uitgedroogd, scheiding van olien)</li>
        <li>Veranderde kleur</li>
        <li>Irritatie bij gebruik die je eerder niet had</li>
      </ul>

      <h2>Tips voor langere houdbaarheid</h2>
      <p>Bewaar je make-up op een koele, droge plek, uit direct zonlicht. Sluit producten altijd goed af na gebruik. Was je handen voordat je producten aanraakt en houd je kwasten en sponsjes schoon.</p>

      <h2>Waarom dit extra belangrijk is bij gevoelige huid</h2>
      <p>Verlopen cosmetica kan bacterien en schimmels bevatten die huidirritatie, infecties en allergische reacties kunnen veroorzaken. Als je al een gevoelige huid hebt, is het extra belangrijk om je producten tijdig te vervangen en goede hygiene te handhaven.</p>
    `,
    date: "2024-07-08",
    author: "Team Unity Cosmetics",
    category: "Informatie",
    featuredImage: `${CDN}/gezicht/poeder/compact-poeder-100-translucent.webp`,
  },
];

export function getBlogPostBySlug(slug: string): BlogPost | undefined {
  return blogPosts.find((p) => p.slug === slug);
}

export function getRelatedPosts(currentSlug: string, limit = 3): BlogPost[] {
  const current = getBlogPostBySlug(currentSlug);
  if (!current) return blogPosts.slice(0, limit);

  // Prefer same category, then recent
  const sameCategory = blogPosts.filter(
    (p) => p.slug !== currentSlug && p.category === current.category
  );
  const other = blogPosts.filter(
    (p) => p.slug !== currentSlug && p.category !== current.category
  );

  return [...sameCategory, ...other].slice(0, limit);
}

export function formatDate(dateStr: string, locale: string = "nl"): string {
  const date = new Date(dateStr);
  // Map app locale code to a BCP-47 tag the browser understands
  const bcp47 = ({ nl: "nl-NL", de: "de-DE", en: "en-GB", fr: "fr-FR", es: "es-ES", it: "it-IT" } as const)[locale as "nl" | "de" | "en" | "fr" | "es" | "it"] ?? "en-GB";
  return date.toLocaleDateString(bcp47, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}
